import React, { memo, useCallback } from "react";
import {
  Box,
  Button,
  Divider,
  IconButton,
  List,
  ListItem,
  MenuItem,
  Paper,
  Popover,
  TextField,
  Tooltip,
  Typography,
  FormControlLabel,
  Switch,
  Chip,
} from "@mui/material";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import ArrowDropDownOutlinedIcon from "@mui/icons-material/ArrowDropDownOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { styled } from "@mui/material/styles";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import SectionLabel from "../../widgets/SectionLabel";
import { GlassButton } from "../../themes/UiElements";

/* --------------------------------- styling --------------------------------- */
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: theme.shadows[0],
  backgroundColor: theme.palette.background.paper,
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiInputBase-root": { borderRadius: theme.shape.borderRadius },
  "& .MuiInputLabel-root": { fontSize: "0.9rem" },
  width: "100%",
}));

/* ---------------------- pure memo child components ------------------------- */

const MenuSection = memo(function MenuSection({
  title,
  items,
  type,
  disabledCheck,
  onSelect,
}) {
  return (
    <Box sx={{ px: 2, py: 1.5 }}>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
        {title}
      </Typography>
      {items.map((itm) => (
        <MenuItem
          key={`${type}-${itm.name}`}
          disabled={disabledCheck(itm)}
          onClick={() => onSelect(type, itm)}
        >
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {itm.name}
            </Typography>
            {itm.subtext && (
              <Typography variant="caption" color="text.secondary">
                {itm.subtext}
              </Typography>
            )}
          </Box>
        </MenuItem>
      ))}
    </Box>
  );
});

const InputBlock = memo(function InputBlock({
  button,
  meta,
  onFieldChange,
  onRemove,
  isDragging,
  dragHandleProps,
}) {
  const update = (field) => (e) => {
    let value = e.target.value;
    // Enforce 20 character limit for catalog button text
    if (field === "text" && meta.type === "catalog" && value.length > 20) {
      value = value.substring(0, 20);
    }
    onFieldChange(button.id, field, value);
  };

  const toggleDynamic = () => {
    onFieldChange(button.id, "isDynamic", !button.isDynamic);
  };

  const addVariable = () => {
    // Only allow one variable for URL buttons
    if (button.urlVariable) return;
    onFieldChange(button.id, "urlVariable", { name: "", example: "" });
  };

  const updateVariable = (field, value) => {
    const variable = { ...(button.urlVariable || {}), [field]: value };
    onFieldChange(button.id, "urlVariable", variable);
  };

  const removeVariable = () => {
    onFieldChange(button.id, "urlVariable", null);
  };

  return (
    <Box
      sx={{
        borderRadius: 2,
        mt: 2,
        display: "flex",
        flexDirection: "column",
        gap: 2,
        backgroundColor: isDragging ? "action.hover" : "transparent",
        transition: "background-color 0.2s",
        p: 2,
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
        <Box
          {...dragHandleProps}
          sx={{
            display: "flex",
            alignItems: "center",
            cursor: "grab",
            color: "text.secondary",
            "&:active": { cursor: "grabbing" },
            pt: 1,
          }}
        >
          <DragIndicatorIcon fontSize="small" />
        </Box>

        <StyledTextField
          label="Button text"
          value={button.text}
          onChange={update("text")}
          size="small"
          helperText={
            meta.type === "catalog"
              ? `${button.text?.length || 0}/20 characters`
              : undefined
          }
          inputProps={meta.type === "catalog" ? { maxLength: 20 } : undefined}
        />

        <Tooltip title="Remove">
          <Box>
            <IconButton
              onClick={() => onRemove(button)}
              sx={{ color: "error.main", ml: "auto" }}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Box>
        </Tooltip>
      </Box>

      {meta.type === "url" && (
        <Box sx={{ pl: 5 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={button.isDynamic || false}
                  onChange={toggleDynamic}
                  size="small"
                />
              }
              label={
                <Typography variant="body2">
                  Dynamic URL (with variables)
                </Typography>
              }
            />
            {button.isDynamic && (
              <Chip
                label="Dynamic"
                size="small"
                color="primary"
                variant="outlined"
              />
            )}
          </Box>

          <StyledTextField
            label="URL"
            value={button.url || ""}
            onChange={update("url")}
            size="small"
            helperText={
              button.isDynamic
                ? "Use {{1}}, {{2}}, etc. for variables"
                : "Enter a static URL"
            }
          />

          {button.isDynamic && (
            <Box sx={{ mt: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 1,
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Variable (Max 1)
                </Typography>
                {!button.urlVariable && (
                  <Button
                    size="small"
                    startIcon={<AddOutlinedIcon />}
                    onClick={addVariable}
                    sx={{ textTransform: "none" }}
                  >
                    Add Variable
                  </Button>
                )}
              </Box>

              {button.urlVariable && (
                <Box
                  sx={{
                    display: "flex",
                    gap: 1,
                    alignItems: "flex-start",
                    mb: 1,
                    p: 1.5,
                    backgroundColor: "action.hover",
                    borderRadius: 1,
                  }}
                >
                  <Chip label="{{1}}" size="small" sx={{ mt: 1 }} />
                  <StyledTextField
                    label="Variable Name"
                    value={button.urlVariable.name || ""}
                    onChange={(e) => updateVariable("name", e.target.value)}
                    size="small"
                    placeholder="e.g., mob, user_id"
                  />
                  <StyledTextField
                    label="Example Value"
                    value={button.urlVariable.example || ""}
                    onChange={(e) => updateVariable("example", e.target.value)}
                    size="small"
                    placeholder="e.g., 9538077099"
                  />
                  <IconButton
                    size="small"
                    onClick={removeVariable}
                    sx={{ color: "error.main", mt: 0.5 }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}

              {!button.urlVariable && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mt: 1 }}
                >
                  No variable added yet. Click "Add Variable" to define one
                  variable for your URL.
                </Typography>
              )}
            </Box>
          )}
        </Box>
      )}

      {meta.type === "whatsapp_call" && (
        <Box sx={{ pl: 5 }}>
          <Chip
            label="Call on WhatsApp"
            color="success"
            size="small"
            variant="outlined"
            sx={{ mb: 1 }}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Customers can call your business directly in WhatsApp. Calls go to
            the WhatsApp business number sending this template. WhatsApp Calling
            must be enabled for that number.
          </Typography>

          <StyledTextField
            select
            label="Call button expires after"
            size="small"
            value={Number(button.ttlMinutes ?? 10080)}
            onChange={(event) =>
              onFieldChange(button.id, "ttlMinutes", Number(event.target.value))
            }
            helperText="Controls how long the call button remains available to the recipient."
          >
            {/* Preserve saved minute-based expiry values between whole days. */}
            {button.ttlMinutes != null &&
              Number(button.ttlMinutes) % 1440 !== 0 && (
                <MenuItem value={Number(button.ttlMinutes)}>
                  Existing setting — {button.ttlMinutes} minutes
                </MenuItem>
              )}

            {Array.from({ length: 30 }, (_, index) => {
              const days = index + 1;
              return (
                <MenuItem key={days} value={days * 1440}>
                  {days} {days === 1 ? "day" : "days"}
                  {days === 7 ? " (Default)" : ""}
                </MenuItem>
              );
            })}
          </StyledTextField>
        </Box>
      )}

      {meta.type === "phone" && (
        <Box sx={{ pl: 5 }}>
          <StyledTextField
            label="Phone number"
            value={button.phone || ""}
            onChange={update("phone")}
            size="small"
          />
        </Box>
      )}

      {meta.type === "code" && (
        <Box sx={{ pl: 5 }}>
          <StyledTextField
            label="Offer code"
            value={button.code || ""}
            onChange={update("code")}
            size="small"
          />
        </Box>
      )}

      {meta.type === "flow" && (
        <Box sx={{ pl: 5 }}>
          <StyledTextField
            label="Flow ID"
            helperText={
              <Typography variant="body1">
                <a href="/home/flows" target="_blank">
                  Click here
                </a>{" "}
                for the flow id
              </Typography>
            }
            value={button.flowId || ""}
            onChange={update("flowId")}
            size="small"
          />
        </Box>
      )}

      {meta.type === "catalog" && (
        <Box sx={{ pl: 5 }}>
          <Typography variant="caption" color="text.secondary">
            This button will display your WhatsApp catalog. The button text can
            be customized up to 20 characters.
          </Typography>
        </Box>
      )}
    </Box>
  );
});

/* ------------------------- main controlled component ----------------------- */

function ButtonTemplateCreator({
  value = [],
  onChange,
  category = "MARKETING",
  // Opt in from the standard editor so carousel consumers keep their options.
  allowWhatsAppCall = false,
  /* ui / limits props with defaults */
  initialCTAItems = [
    {
      name: "Visit website",
      maxButtons: 2,
      subtext: "2 buttons max",
      type: "url",
    },
    {
      name: "Call Phone",
      maxButtons: 1,
      subtext: "1 button max",
      type: "phone",
    },
    {
      name: "Complete flow",
      maxButtons: 1,
      subtext: "1 button max",
      type: "flow",
    },
    {
      name: "Copy offer code",
      maxButtons: 1,
      subtext: "1 button max",
      type: "code",
    },
    {
      name: "View catalog",
      maxButtons: 1,
      subtext: "1 button max",
      type: "catalog",
    },
  ],
  quickReplyLabel = "Quick-reply",
  ctaLabel = "Call-to-action",
  addButtonLabel = "Add button",
  maxQuickReplies = 6,
}) {
  const ctaItems = React.useMemo(() => {
    const supportsCall =
      allowWhatsAppCall &&
      ["MARKETING", "UTILITY"].includes(category?.toUpperCase());
    const items = initialCTAItems.filter(
      (item) => item.type !== "whatsapp_call" || supportsCall
    );
    if (supportsCall && !items.some((item) => item.type === "whatsapp_call")) {
      const index = items.findIndex((item) => item.type === "phone");
      items.splice(index < 0 ? items.length : index + 1, 0, {
        name: "Call on WhatsApp",
        type: "whatsapp_call",
        maxButtons: 1,
        subtext: "Let customers call your business in WhatsApp",
      });
    }
    return items;
  }, [initialCTAItems, allowWhatsAppCall, category]);

  /* pop-over anchor */
  const [anchorEl, setAnchorEl] = React.useState(null);
  const openMenu = (e) => setAnchorEl(e.currentTarget);
  const closeMenu = () => setAnchorEl(null);

  /* derive quick/cta arrays & counts from controlled value */
  const quickReplies = value.filter((b) => b.group === "quickReply");
  const ctaButtons = value.filter((b) => b.group === "cta");

  const ctaCounts = React.useMemo(() => {
    const counts = Object.fromEntries(ctaItems.map((i) => [i.type, 0]));
    ctaButtons.forEach((b) => {
      counts[b.type] = (counts[b.type] || 0) + 1;
    });
    return counts;
  }, [ctaButtons, ctaItems]);

  /* --------------- handlers --------------- */
  const addButton = useCallback(
    (type, itemMeta) => {
      if (type === "quickReply" && quickReplies.length >= maxQuickReplies)
        return;
      if (type === "cta" && ctaCounts[itemMeta.type] >= itemMeta.maxButtons)
        return;
      const id = `${type}-${itemMeta.name}-${Date.now()}`;
      const newBtn = {
        id,
        group: type,
        name: itemMeta.name,
        type: itemMeta.type || "text",
        text: itemMeta.type === "catalog" ? "View catalog" : itemMeta.name,
        isDynamic: false,
        urlVariable: null, // Single variable object: { name, example }
        ...(itemMeta.type === "whatsapp_call" && { ttlMinutes: 10080 }),
      };
      onChange([...value, newBtn]);
      closeMenu();
    },
    [ctaCounts, maxQuickReplies, onChange, quickReplies.length, value]
  );

  const removeButton = useCallback(
    (btn) => {
      onChange(value.filter((b) => b.id !== btn.id));
    },
    [onChange, value]
  );

  const updateField = useCallback(
    (id, field, fieldValue) => {
      const apiFields = {
        text: "text",
        url: "url",
        phone: "phone_number",
        flowId: "flow_id",
        code: "code",
      };
      const next = value.map((b) => {
        if (b.id !== id) return b;
        let originalButton = b.originalButton;
        if (originalButton && apiFields[field]) {
          originalButton = {
            ...originalButton,
            [apiFields[field]]: fieldValue,
          };
        }
        // Use the existing URL builder after the user edits dynamic URL settings.
        if (["isDynamic", "urlVariable"].includes(field))
          originalButton = undefined;
        return { ...b, [field]: fieldValue, originalButton };
      });
      onChange(next);
    },
    [onChange, value]
  );

  const handleDragEnd = useCallback(
    (result) => {
      if (!result.destination) return;

      const { source, destination } = result;

      // Reorder the entire value array
      const reordered = Array.from(value);
      const [removed] = reordered.splice(source.index, 1);
      reordered.splice(destination.index, 0, removed);

      onChange(reordered);
    },
    [onChange, value]
  );

  // Helper to get meta info for any button
  const getButtonMeta = useCallback(
    (button) => {
      if (button.group === "quickReply") {
        return { type: "text" };
      }
      return (
        ctaItems.find((i) => i.type === button.type) || { type: button.type }
      );
    },
    [ctaItems]
  );

  /* ----------------------------- render ----------------------------- */
  return (
    <StyledPaper sx={{ maxWidth: 760, mx: "auto" }}>
      <GlassButton
        variant="contained"
        startIcon={<AddOutlinedIcon />}
        endIcon={<ArrowDropDownOutlinedIcon />}
        onClick={openMenu}
        sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}
      >
        {addButtonLabel}
      </GlassButton>

      {/* pop-over menu */}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={closeMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{ sx: { borderRadius: 2, width: 320 } }}
      >
        <MenuSection
          title={quickReplyLabel}
          type="quickReply"
          items={[
            {
              name: "Quick reply",
              maxButtons: maxQuickReplies,
              type: "text",
            },
          ]}
          disabledCheck={() => quickReplies.length >= maxQuickReplies}
          onSelect={addButton}
        />
        <Divider />
        <MenuSection
          title={ctaLabel}
          type="cta"
          items={ctaItems}
          disabledCheck={(itm) => ctaCounts[itm.type] >= itm.maxButtons}
          onSelect={addButton}
        />
      </Popover>

      <Box sx={{ mt: 2 }}>
        <SectionLabel>Selected ({value.length})</SectionLabel>

        {value.length > 0 && (
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="all-buttons">
              {(provided, snapshot) => (
                <List
                  dense
                  disablePadding
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  sx={{
                    backgroundColor: snapshot.isDraggingOver
                      ? "action.hover"
                      : "transparent",
                    borderRadius: 2,
                    transition: "background-color 0.2s",
                    p: 1,
                  }}
                >
                  {value.map((btn, index) => (
                    <Draggable key={btn.id} draggableId={btn.id} index={index}>
                      {(provided, snapshot) => (
                        <ListItem
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          disableGutters
                          sx={{ display: "block" }}
                        >
                          <InputBlock
                            button={btn}
                            meta={getButtonMeta(btn)}
                            onFieldChange={updateField}
                            onRemove={removeButton}
                            isDragging={snapshot.isDragging}
                            dragHandleProps={provided.dragHandleProps}
                          />
                        </ListItem>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </List>
              )}
            </Droppable>
          </DragDropContext>
        )}
      </Box>
    </StyledPaper>
  );
}

export default memo(ButtonTemplateCreator);
